// ============================================================================
// DEPOSITSCHEME COMPONENT - Simplified Interest Type Selection Modal
// ============================================================================
import React, { useState } from "react";
import { Modal, Button, Form, Alert, Spinner, Card, Row, Col } from "react-bootstrap";

const DepositScheme = ({
  show,
  onHide,
  onConfirm,
  paymentAmount,
  accountNumber,
}) => {
  const [selectedType, setSelectedType] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");

  // Interest options
  const options = [
    {
      value: "monthly",
      label: "Monthly Interest",
      desc: "Interest paid monthly",
      icon: "📅"
    },
    {
      value: "yearly",
      label: "Yearly Interest", 
      desc: "Interest compounded annually",
      icon: "📈"
    },
  ];

  const handleConfirm = async () => {
    if (!selectedType) {
      setError("Please select an interest payment option.");
      return;
    }

    setIsProcessing(true);
    try {
      await onConfirm({
        interestType: selectedType,
        paymentAmount,
        accountNumber,
      });
    } catch (err) {
      setError("Failed to process selection. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setSelectedType("");
      setError("");
      onHide();
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      centered 
      backdrop={isProcessing ? "static" : true}
    >
      <Modal.Header closeButton={!isProcessing}>
        <Modal.Title className="d-flex align-items-center">
          <span className="me-2">🏦</span>
          Interest Payment Option
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Account Info */}
        <Card className="mb-3 border-0 bg-light">
          <Card.Body className="py-2">
            <Row className="align-items-center">
              <Col>
                <small className="text-muted">Account Number</small>
                <div className="fw-semibold">{accountNumber}</div>
              </Col>
              <Col className="text-end">
                <small className="text-muted">Amount</small>
                <div className="fw-semibold text-success">
                  ₹{paymentAmount?.toLocaleString()}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" className="py-2 mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {/* Instructions */}
        <div className="mb-3">
          <Form.Label className="fw-semibold">Choose interest payment frequency:</Form.Label>
          <small className="text-muted d-block">Select how you want to receive interest</small>
        </div>

        {/* Options - Fixed the nested loop issue */}
        <Form>
          <div className="d-grid gap-3">
            {options.map((option) => (
              <Card
                key={option.value}
                className={`border-2 ${
                  selectedType === option.value
                    ? "border-primary bg-primary bg-opacity-10"
                    : "border-light hover-shadow"
                }`}
                style={{ 
                  cursor: "pointer", 
                  transition: "all 0.2s ease"
                }}
                onClick={() => {
                  setSelectedType(option.value);
                  setError("");
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-start">
                    {/* Radio button */}
                    <Form.Check
                      type="radio"
                      name="interestType"
                      id={option.value}
                      value={option.value}
                      checked={selectedType === option.value}
                      onChange={(e) => {
                        setSelectedType(e.target.value);
                        setError("");
                      }}
                      className="me-3 mt-1"
                      style={{ transform: "scale(1.2)" }}
                    />

                    {/* Content */}
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-1">
                        <span className="me-2 fs-5">{option.icon}</span>
                        <h6 className="mb-0">{option.label}</h6>
                      </div>
                      <div className="small text-muted">{option.desc}</div>
                    </div>

                    {/* Selection indicator */}
                    {selectedType === option.value && (
                      <div className="text-primary">
                        <i className="bi bi-check-circle-fill fs-5"></i>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        </Form>

        {/* Selected Summary */}
        {selectedType && (
          <Card className="mt-3 border-success">
            <Card.Body className="py-2 bg-success bg-opacity-10">
              <div className="d-flex align-items-center">
                <span className="text-success me-2">✅</span>
                <div>
                  <strong className="text-success">
                    Selected: {options.find((o) => o.value === selectedType)?.label}
                  </strong>
                  <div className="small text-muted">
                    Updated
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!selectedType || isProcessing}
        >
          {isProcessing ? (
            <>
              <Spinner size="sm" className="me-2" />
              Processing...
            </>
          ) : (
            "Continue"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DepositScheme;
